import React, { useEffect, useState } from 'react'
import { Box, Typography, CircularProgress, Button, Paper } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { useMutation, gql } from '@apollo/client'
import { useLocation } from 'react-router-dom'
import Logo from '../assets/img/rushmyfood-logo.png'

const VERIFY_VENDOR_EMAIL = gql`
  mutation VerifyVendorEmail($token: String!) {
    verifyVendorEmail(token: $token) {
      result
      message
    }
  }
`

// Module scope so it survives a remount of this component: if the page mounts
// again while the request is still in the air, both mounts wait on the same
// promise instead of sending a second verify for the same token.
const inFlight = new Map()

function VerifyEmail() {
  const [status, setStatus] = useState('loading') // loading | success | error
  const [message, setMessage] = useState('')
  const location = useLocation()

  const [verifyVendorEmail] = useMutation(VERIFY_VENDOR_EMAIL)

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const token = params.get('token')
    if (!token) {
      setStatus('error')
      setMessage('Invalid verification link.')
      return
    }

    let cancelled = false
    let request = inFlight.get(token)
    if (!request) {
      request = verifyVendorEmail({ variables: { token } })
      inFlight.set(token, request)
    }

    request
      .then(({ data }) => {
        if (cancelled) return
        if (data?.verifyVendorEmail?.result) {
          setStatus('success')
          setMessage(data.verifyVendorEmail.message || 'Email verified successfully!')
        } else {
          setStatus('error')
          setMessage(data?.verifyVendorEmail?.message || 'Verification failed.')
        }
      })
      .catch(err => {
        if (cancelled) return
        setStatus('error')
        setMessage(err.message || 'Something went wrong.')
      })

    return () => { cancelled = true }
  }, [])

  return (
    <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh" bgcolor="#f5f5f5">
      <Paper elevation={3} sx={{ p: 5, maxWidth: 440, width: '100%', textAlign: 'center', borderRadius: 3 }}>
        <img src={Logo} alt="RushmyFood" style={{ height: 60, marginBottom: 24 }} />
        {status === 'loading' && (
          <>
            <CircularProgress color="primary" sx={{ mb: 2 }} />
            <Typography variant="h6">Verifying your email...</Typography>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" fontWeight={700} mb={1}>Email Verified!</Typography>
            <Typography color="text.secondary" mb={3}>{message}</Typography>
            <Button variant="contained" color="primary" href="/#/auth/login" fullWidth>
              Go to Login
            </Button>
          </>
        )}
        {status === 'error' && (
          <>
            <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" fontWeight={700} mb={1}>Verification Failed</Typography>
            <Typography color="text.secondary" mb={3}>{message}</Typography>
            <Button variant="outlined" color="primary" href="/#/auth/login" fullWidth>
              Go to Login
            </Button>
          </>
        )}
      </Paper>
    </Box>
  )
}

export default VerifyEmail
